// MACHINERY V2 (Stroje) — the Settings-idiom ✕ with a legal touch target
// (M2.16e).
//
// >>> THE PAINTED BOX AND THE HIT TARGET ARE NOT THE SAME BOX BELOW 1280, AND
// THAT IS THE WHOLE POINT. <<< The idiom being matched is
// SettingsV2DialogShell.tsx:226-235: a `<button className="btn btn-ghost">`
// holding the `×` CHARACTER — not an SVG — at `{ width: 30, padding: 0,
// fontSize: 15 }`. Below 1280 that control owes the module's ">= 44px on BOTH
// axes" law a target it cannot reach by GROWING: a 44px `.btn-ghost` paints a
// 44x44 accent square on hover where the artboard draws a 30x30 one. So below
// 1280 the button becomes a transparent 44px hit area and the artboard's paint
// becomes a `<span>` child of it — the same shape mobile/TouchChip.tsx,
// profile/tabs/partsAtoms.tsx's checkbox and specSheetToolbar's `Segment`
// already use for a chip, a checkbox and a segment.
//
// THE PAINT IS NOT `.btn` BELOW 1280, AND IT CANNOT BE. The module-wide floor
// (mobile/machinery-v2-touch.scss) matches ANY element carrying `.btn`, not
// merely a `<button>`, so a painted child that kept the class would inflate
// exactly as its parent used to. The child is unclassed and takes its whole
// paint — 30x30, 7px radius, 15px glyph, `--muted`, the hover fill and the
// focus ring — from `.mv2-x-hit` in that sheet. Read that block before
// changing anything here.
//
// AT >= 1280 IT IS TODAY'S ELEMENT, BYTE FOR BYTE: one `.btn btn-ghost` button
// with the four inline values above and no wrapper. That branch is not
// symmetry for its own sake — `.btn:focus-visible` is where this control's
// keyboard ring comes from, so moving the paint off `.btn` at desktop would
// silently delete a ring the desktop DOM has today.
//
// WHY IT IS A COMPONENT. Two dialogs paint this ✕ identically
// (profile/tabs/TabDialog.tsx, readiness/readinessPopupSections.tsx) and both
// sit near the 300-line cap; a per-file copy of the branch is two places to
// get the touch law wrong, which is exactly the drift M2.16e exists to undo.
//
// WHY fleet/wall/WallExitButton.tsx IS NOT A CONSUMER, and is deliberately
// left alone (the same call mobile/TouchChip.tsx records making about
// ActiveFilterChips): the wall's X owes 44px at EVERY width, not only below
// 1280, and its >= 1280 host pins it absolutely against a 30px box — so its
// desktop branch reaches 44 through a transparent absolutely-positioned child
// rather than by being 44. Its below-1280 branch is this one, expressed in the
// same three lines of `.mv2-x-hit` markup; the arithmetic that differs is
// documented in that file's own header.
//
// `asChild` SAFE. Radix `Dialog.Close asChild` clones this component and hands
// it `type` + `onClick`, so the forwarded ref and the `...rest` spread below
// are load-bearing: they are what puts the close handler on the interactive
// node. The paint is a `<span>` — nothing interactive is nested inside
// anything interactive, and keyboard activation stays the button's, once.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { V2_BELOW_DESKTOP, useV2MediaQuery } from "../useV2MediaQuery";

export interface TouchCloseXProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Already-translated accessible name. A screen reader announcing "times,
   *  button" tells nobody anything, so the glyph is never the only label. */
  label: string;
}

const TouchCloseX = React.forwardRef<HTMLButtonElement, TouchCloseXProps>(
  ({ label, ...rest }, ref) => {
    const belowDesktop = useV2MediaQuery(V2_BELOW_DESKTOP);

    // `...rest` FIRST, this component's own props after: `rest` is where Radix's
    // `onClick` (and `type`) arrive, and this control owns its className and its
    // paint outright. Spreading it last would let an undefined `className`
    // travelling in the clone blank out the class the whole paint hangs on.
    if (belowDesktop) {
      return (
        <button {...rest} ref={ref} type="button" className="mv2-x-hit" aria-label={label}>
          {/* Unclassed on purpose: the module-wide floor matches ANY `.btn`, so
              a painted child carrying it would inflate exactly as its parent
              used to. Its whole paint is `.mv2-x-hit > span`. */}
          <span>×</span>
        </button>
      );
    }

    return (
      <button
        {...rest}
        ref={ref}
        type="button"
        className="btn btn-ghost"
        aria-label={label}
        style={{ width: 30, padding: 0, fontSize: 15, justifyContent: "center" }}
      >
        ×
      </button>
    );
  }
);

TouchCloseX.displayName = "TouchCloseX";

export default TouchCloseX;
